import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { CreateUserDto, Role, UpdateUserDto, User } from '../../services/user.service';
import { Field, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { usePlantScope } from '../../hooks/usePlantScope';

// The same rules the server checks
export const PASSWORD_RULES: { label: string; test: (value: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (value) => value.length >= 8 },
  { label: 'An uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'A lowercase letter', test: (value) => /[a-z]/.test(value) },
  { label: 'A number', test: (value) => /\d/.test(value) },
  { label: 'One of @ $ ! % * ? &', test: (value) => /[@$!%*?&]/.test(value) },
];

export const isPasswordValid = (value: string) =>
  PASSWORD_RULES.every((rule) => rule.test(value)) && /^[A-Za-z\d@$!%*?&]/.test(value) && value.length <= 100;

// A random password that passes the rules
export const generatePassword = () => {
  const sets = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '@$!%*?&'];
  const pick = (chars: string) => chars[crypto.getRandomValues(new Uint32Array(1))[0] % chars.length];
  const letters = sets[0] + sets[1] + sets[2];
  // Starts with a letter, then one of each kind, then filled up
  const rest = [pick(sets[1]), pick(sets[2]), pick(sets[3]), ...Array.from({ length: 8 }, () => pick(letters))];
  for (let index = rest.length - 1; index > 0; index--) {
    const swap = crypto.getRandomValues(new Uint32Array(1))[0] % (index + 1);
    [rest[index], rest[swap]] = [rest[swap], rest[index]];
  }
  return pick(sets[0]) + rest.join('');
};

export const PasswordChecklist: React.FC<{ value: string }> = ({ value }) => (
  <ul className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
    {PASSWORD_RULES.map((rule) => {
      const met = rule.test(value);
      return (
        <li key={rule.label} className={cn('flex items-center gap-1.5', met ? 'text-green-700' : 'text-gray-500')}>
          <CheckCircleIcon className={cn('h-4 w-4', met ? 'text-green-600' : 'text-gray-300')} />
          {rule.label}
        </li>
      );
    })}
  </ul>
);

export const PasswordInput: React.FC<{
  id: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  autoComplete?: string;
}> = ({ id, value, onChange, invalid, autoComplete = 'new-password' }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
        className={cn(inputClass, 'pr-10', invalid && inputErrorClass)}
      />
      <button
        type="button"
        onClick={() => setVisible((prev) => !prev)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
      >
        {visible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
    </div>
  );
};

// A numbered group of related fields
const Section: React.FC<{ step: number; title: string; description: string; children: React.ReactNode }> = ({
  step,
  title,
  description,
  children,
}) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
        {step}
      </span>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', hint: 'Can sign in' },
  { value: 'pending', label: 'Pending', hint: 'Waiting for email verification' },
  { value: 'suspended', label: 'Suspended', hint: 'Blocked from signing in' },
  { value: 'inactive', label: 'Inactive', hint: 'No longer uses the system' },
] as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const PHONE_PATTERN = /^[0-9+()\-\s]{6,20}$/;

export interface UserFormProps {
  // Edits this user when set; otherwise a new user is added
  user?: User | null;
  roles: Role[];
  // The signed-in user: their own role and status cannot be changed, and only admins hand out the admin role
  currentUserId?: string;
  currentUserRole?: string;
  currentUserPermissions?: string[];
  isSaving: boolean;
  onCancel: () => void;
  onCreate: (data: CreateUserDto) => void;
  onUpdate: (data: UpdateUserDto) => void;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  roles,
  currentUserId,
  currentUserRole,
  currentUserPermissions,
  isSaving,
  onCancel,
  onCreate,
  onUpdate,
}) => {
  const isEdit = !!user;
  const { scope: plantScope } = usePlantScope();
  const isSelf = isEdit && user?.id === currentUserId;
  const { data: portalParties = [] } = useQuery<{ id: string; name: string; code: string }[]>({
    queryKey: ['portal-parties'],
    queryFn: async () => {
      const [customers, vendors] = await Promise.all([api.get('/customers', { params: { isActive: true } }), api.get('/vendors/active')]);
      const rows = (value: any) => (Array.isArray(value) ? value : value?.data ?? []);
      return [
        ...rows(customers.data).map((row: any) => ({ id: row.id, name: row.name, code: row.code, kind: 'customer' })),
        ...rows(vendors.data).map((row: any) => ({ id: row.id, name: row.name, code: row.code, kind: 'vendor' })),
      ];
    },
  });

  const [form, setForm] = useState({
    firstName: user?.firstName ?? '',
    middleName: user?.middleName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    mobile: user?.mobile ?? '',
    username: user?.username ?? '',
    password: '',
    confirmPassword: '',
    employeeId: user?.employeeId ?? '',
    department: user?.department ?? '',
    plantIds: user?.plantIds ?? ([] as string[]),
    designation: user?.designation ?? '',
    userType: user?.userType ?? 'internal',
    roleId: user?.role?.id ?? '',
    portalKind: (user?.portalVendorId ? 'vendor' : 'customer') as 'customer' | 'vendor',
    portalPartyId: user?.portalCustomerId ?? user?.portalVendorId ?? '',
    status: user?.status ?? 'active',
    requireEmailVerification: false,
    sendWelcomeEmail: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isPortalRole = roles.find((role) => role.id === form.roleId)?.name === 'portal';

  const setField = <K extends keyof typeof form>(name: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Inactive roles cannot be assigned; the user's own role stays in the list so it shows
  const roleOptions = roles.filter((role) => {
    if (role.id === user?.role?.id) return true;
    if (!role.isActive) return false;
    if (currentUserRole === 'admin') return true;
    // Nobody hands out a role that may do more than they can themselves
    return role.name !== 'admin' && (!currentUserPermissions || (role.permissions ?? []).every((code) => currentUserPermissions.includes(code)));
  });

  const fieldClass = (name: string) => cn(inputClass, errors[name] && inputErrorClass);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = 'Enter the first name';
    if (!form.lastName.trim()) next.lastName = 'Enter the last name';
    if (!form.email.trim()) next.email = 'Enter the email address';
    else if (!EMAIL_PATTERN.test(form.email.trim())) next.email = 'Enter a valid email address';
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) next.phone = 'Enter a valid phone number';
    if (form.mobile.trim() && !PHONE_PATTERN.test(form.mobile.trim())) next.mobile = 'Enter a valid mobile number';
    const username = form.username.trim();
    if (username || isEdit) {
      if (username.length < 3 || username.length > 30) next.username = 'Use 3 to 30 characters';
      else if (!USERNAME_PATTERN.test(username)) next.username = 'Use letters, numbers, hyphens and underscores only';
    }
    if (!isEdit) {
      if (!form.password) next.password = 'Enter a password';
      else if (!isPasswordValid(form.password)) next.password = 'The password does not meet the rules below';
      if (form.confirmPassword !== form.password) next.confirmPassword = 'The passwords do not match';
    }
    if (!form.roleId) next.roleId = 'Choose a role';
    if (isPortalRole && !form.portalPartyId) next.portalPartyId = `Choose the ${form.portalKind} whose portal this is`;
    setErrors(next);
    return next;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const found = validate();
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`user-${first}`)?.focus();
      return;
    }

    const shared = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      mobile: form.mobile.trim(),
      employeeId: form.employeeId.trim(),
      department: form.department.trim(),
      plantIds: form.plantIds,
      designation: form.designation.trim(),
      userType: isPortalRole ? 'external' : form.userType,
      roleId: form.roleId,
      // A portal account belongs to one customer or one vendor; any other account to none
      portalCustomerId: isPortalRole && form.portalKind === 'customer' ? form.portalPartyId : null,
      portalVendorId: isPortalRole && form.portalKind === 'vendor' ? form.portalPartyId : null,
    };

    if (isEdit) {
      onUpdate({
        ...shared,
        username: form.username.trim().toLowerCase(),
        // Your own status is not sent: it cannot be changed here
        ...(isSelf ? {} : { status: form.status }),
      });
    } else {
      onCreate({
        ...shared,
        ...(form.username.trim() ? { username: form.username.trim().toLowerCase() } : {}),
        password: form.password,
        // With a verification link the user starts as pending; otherwise they can sign in right away
        ...(form.requireEmailVerification ? {} : { status: 'active' as const }),
        requireEmailVerification: form.requireEmailVerification,
        sendWelcomeEmail: form.sendWelcomeEmail,
      });
    }
  };

  const errorCount = Object.keys(errors).length;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <Section step={1} title="Personal details" description="Who the user is and how to reach them.">
        <Field label="First Name" htmlFor="user-firstName" required error={errors.firstName}>
          <input id="user-firstName" type="text" maxLength={50} autoFocus={!isEdit} value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} className={fieldClass('firstName')} />
        </Field>
        <Field label="Last Name" htmlFor="user-lastName" required error={errors.lastName}>
          <input id="user-lastName" type="text" maxLength={50} value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} className={fieldClass('lastName')} />
        </Field>
        <Field label="Middle Name" htmlFor="user-middleName">
          <input id="user-middleName" type="text" maxLength={50} value={form.middleName} onChange={(e) => setField('middleName', e.target.value)} className={inputClass} />
        </Field>
        <Field
          label="Email"
          htmlFor="user-email"
          required
          error={errors.email}
          hint={isEdit ? 'Changing it marks the email as not verified' : 'Used to sign in'}
        >
          <input id="user-email" type="email" maxLength={150} autoComplete="off" value={form.email} onChange={(e) => setField('email', e.target.value)} className={fieldClass('email')} />
        </Field>
        <Field label="Phone" htmlFor="user-phone" error={errors.phone}>
          <input id="user-phone" type="tel" maxLength={20} value={form.phone} onChange={(e) => setField('phone', e.target.value)} className={fieldClass('phone')} />
        </Field>
        <Field label="Mobile" htmlFor="user-mobile" error={errors.mobile}>
          <input id="user-mobile" type="tel" maxLength={20} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} className={fieldClass('mobile')} />
        </Field>
      </Section>

      <Section step={2} title="Work details" description="Where the user sits in the organisation.">
        <Field label="Employee ID" htmlFor="user-employeeId">
          <input id="user-employeeId" type="text" maxLength={50} value={form.employeeId} onChange={(e) => setField('employeeId', e.target.value)} className={inputClass} />
        </Field>
        <Field label="User Type" htmlFor="user-userType" hint="External users are people outside the company, such as auditors">
          <select id="user-userType" value={form.userType} onChange={(e) => setField('userType', e.target.value as typeof form.userType)} className={inputClass}>
            <option value="internal">Internal</option>
            <option value="external">External</option>
            <option value="system">System</option>
          </select>
        </Field>
        <Field label="Department" htmlFor="user-department">
          <input id="user-department" type="text" maxLength={100} value={form.department} onChange={(e) => setField('department', e.target.value)} className={inputClass} placeholder="e.g. Production" />
        </Field>
        <Field label="Designation" htmlFor="user-designation">
          <input id="user-designation" type="text" maxLength={100} value={form.designation} onChange={(e) => setField('designation', e.target.value)} className={inputClass} placeholder="e.g. Shift Supervisor" />
        </Field>
        {plantScope.plants.length > 0 && (
          <fieldset className="sm:col-span-2" data-testid="user-plants">
            <legend className="block text-sm font-medium text-gray-700">Plants</legend>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
              {plantScope.plants.map((plant) => (
                <label key={plant.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.plantIds.includes(plant.id)}
                    onChange={(e) => setField('plantIds', e.target.checked ? [...form.plantIds, plant.id] : form.plantIds.filter((id) => id !== plant.id))}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {plant.name}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {form.plantIds.length === 0
                ? 'None ticked: this user sees the sales orders, purchase orders and work orders of every plant.'
                : 'This user sees only the documents of the ticked plants, and those that carry no plant. An administrator always sees every plant.'}
            </p>
          </fieldset>
        )}
      </Section>

      <Section step={3} title="Sign-in and access" description="How the user signs in and what they are allowed to do.">
        <Field
          label="Username"
          htmlFor="user-username"
          required={isEdit}
          error={errors.username}
          hint={isEdit ? 'Can be used instead of the email to sign in' : 'Made from the email when left empty'}
        >
          <input id="user-username" type="text" maxLength={30} autoComplete="off" value={form.username} onChange={(e) => setField('username', e.target.value)} className={fieldClass('username')} />
        </Field>
        <Field
          label="Role"
          htmlFor="user-roleId"
          required
          error={errors.roleId}
          hint={isSelf ? 'You cannot change your own role' : 'Decides which pages and actions the user gets'}
        >
          <select id="user-roleId" value={form.roleId} disabled={isSelf} onChange={(e) => setField('roleId', e.target.value)} className={fieldClass('roleId')}>
            <option value="">Select a role</option>
            {roleOptions.map((role) => (
              <option key={role.id} value={role.id}>
                {role.displayName}
              </option>
            ))}
          </select>
        </Field>

        {isPortalRole && (
          <div className="sm:col-span-2 grid grid-cols-1 gap-4 rounded-md bg-indigo-50 p-4 sm:grid-cols-3" data-testid="portal-party">
            <Field label="Portal for" htmlFor="user-portalKind">
              <select id="user-portalKind" value={form.portalKind} onChange={(e) => { setField('portalKind', e.target.value as 'customer' | 'vendor'); setField('portalPartyId', ''); }} className={inputClass}>
                <option value="customer">A customer</option>
                <option value="vendor">A vendor</option>
              </select>
            </Field>
            <Field label={form.portalKind === 'customer' ? 'Customer' : 'Vendor'} htmlFor="user-portalPartyId" required error={errors.portalPartyId} className="sm:col-span-2" hint="The account sees only this customer's or vendor's own orders, invoices and payments">
              <select id="user-portalPartyId" value={form.portalPartyId} onChange={(e) => setField('portalPartyId', e.target.value)} className={fieldClass('portalPartyId')}>
                <option value="">Choose</option>
                {(portalParties as any[]).filter((party) => party.kind === form.portalKind).map((party) => (
                  <option key={party.id} value={party.id}>{party.name} ({party.code})</option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {isEdit ? (
          <Field
            label="Status"
            htmlFor="user-status"
            hint={isSelf ? 'You cannot change your own status' : STATUS_OPTIONS.find((option) => option.value === form.status)?.hint}
          >
            <select id="user-status" value={form.status} disabled={isSelf} onChange={(e) => setField('status', e.target.value as typeof form.status)} className={inputClass}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <>
            <div className="sm:col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Password" htmlFor="user-password" required error={errors.password}>
                <PasswordInput id="user-password" value={form.password} onChange={(value) => setField('password', value)} invalid={!!errors.password} />
              </Field>
              <Field label="Confirm Password" htmlFor="user-confirmPassword" required error={errors.confirmPassword}>
                <PasswordInput id="user-confirmPassword" value={form.confirmPassword} onChange={(value) => setField('confirmPassword', value)} invalid={!!errors.confirmPassword} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={() => {
                  const password = generatePassword();
                  setForm((prev) => ({ ...prev, password, confirmPassword: password }));
                  setErrors((prev) => {
                    const { password: _p, confirmPassword: _c, ...rest } = prev;
                    return rest;
                  });
                }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Generate a password
              </button>
              <PasswordChecklist value={form.password} />
            </div>
            <div className="sm:col-span-2 space-y-3 border-t border-gray-100 pt-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.sendWelcomeEmail}
                  onChange={(e) => setForm((prev) => ({ ...prev, sendWelcomeEmail: e.target.checked, requireEmailVerification: e.target.checked ? prev.requireEmailVerification : false }))}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Send a welcome email</span>
                  <span className="block text-xs text-gray-500">The email carries the sign-in details, including this password.</span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.requireEmailVerification}
                  disabled={!form.sendWelcomeEmail}
                  onChange={(e) => setField('requireEmailVerification', e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Require email verification</span>
                  <span className="block text-xs text-gray-500">
                    The user stays pending and cannot sign in until they open the link in the welcome email.
                  </span>
                </span>
              </label>
            </div>
          </>
        )}
      </Section>

      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className={cn('text-sm', errorCount > 0 ? 'text-red-600' : 'text-gray-500')}>
          {errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'field needs' : 'fields need'} attention` : 'Fields marked * are required'}
        </p>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add User'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
